/**
 * Dosya transferi (controller → host). Windows UdpFileSender.cs mirror.
 *
 * Wire: FileTransferFrame (12 byte header + payload), Channel = Files (5).
 *   op: 0=Init (JSON {name,size,mime,sha256Hex}),
 *       1=Chunk (offset:u64 BE + data, MAX 1192 byte),
 *       2=ChunkAck (ack_offset:u64 BE + [missing]),
 *       3=Complete, 4=Cancel
 */
import { Channel, PacketType, buildPacket, } from './packet-header';
const MAX_CHUNK = 1192;
export class FileSender {
    bridge;
    seq = 1;
    streamCounter = 0;
    sessionId24;
    /**
     * @param sessionId24 Backend'den gelen relay session ID (24-bit).
     */
    constructor(bridge, sessionId24) {
        this.bridge = bridge;
        this.sessionId24 = sessionId24 & 0x00ffffff;
    }
    async sendFile(file, onProgress) {
        const streamId = this.streamCounter++ & 0x0f;
        // SHA-256 önceden hesapla (integrity doğrulama için host tarafında)
        const buf = await file.arrayBuffer();
        const hashBuf = await crypto.subtle.digest('SHA-256', buf);
        const sha = Array.from(new Uint8Array(hashBuf))
            .map(b => b.toString(16).padStart(2, '0')).join('');
        // Init frame
        const meta = {
            name: file.name,
            size: file.size,
            mime: file.type || null,
            sha256Hex: sha,
        };
        const initJson = new TextEncoder().encode(JSON.stringify(meta));
        this.sendFileFrame(streamId, /*op=Init*/ 0, /*flags*/ 0, /*seq*/ 0, initJson);
        // Chunks
        const data = new Uint8Array(buf);
        let offset = 0;
        let fseq = 1;
        while (offset < data.length) {
            const chunkLen = Math.min(MAX_CHUNK, data.length - offset);
            const payload = new Uint8Array(8 + chunkLen);
            // offset u64 BE (JS'de 53-bit limit; dosyaya yeter — 8 PB)
            payload[0] = 0;
            payload[1] = 0;
            payload[2] = 0;
            payload[3] = 0;
            payload[4] = (offset >>> 24) & 0xff;
            payload[5] = (offset >>> 16) & 0xff;
            payload[6] = (offset >>> 8) & 0xff;
            payload[7] = offset & 0xff;
            payload.set(data.subarray(offset, offset + chunkLen), 8);
            const isLast = (offset + chunkLen) >= data.length;
            this.sendFileFrame(streamId, /*Chunk*/ 1, isLast ? 1 : 0, fseq, payload);
            offset += chunkLen;
            fseq++;
            // Pacing — her 32 chunk'ta bir 1ms yield (ağ doyum)
            if ((fseq & 0x1f) === 0) {
                await new Promise(r => setTimeout(r, 1));
            }
            if (onProgress) {
                onProgress({
                    name: file.name,
                    bytesSent: offset,
                    totalBytes: data.length,
                    percent: (offset / data.length) * 100,
                });
            }
        }
        // Complete
        this.sendFileFrame(streamId, /*Complete*/ 3, 0, fseq, new Uint8Array(0));
        return { ok: true, sha256: sha };
    }
    sendFileFrame(streamId, op, flags, fseq, payload) {
        const frame = new Uint8Array(12 + payload.length);
        frame[0] = 1; // version
        frame[1] = op;
        frame[2] = streamId;
        frame[3] = flags;
        frame[4] = (fseq >>> 24) & 0xff;
        frame[5] = (fseq >>> 16) & 0xff;
        frame[6] = (fseq >>> 8) & 0xff;
        frame[7] = fseq & 0xff;
        frame[8] = (payload.length >>> 24) & 0xff;
        frame[9] = (payload.length >>> 16) & 0xff;
        frame[10] = (payload.length >>> 8) & 0xff;
        frame[11] = payload.length & 0xff;
        frame.set(payload, 12);
        const packet = buildPacket({
            version: 0,
            packetType: PacketType.Data,
            flags: 0,
            sessionId: this.sessionId24,
            sequence: this.seq++,
            channel: Channel.Files,
        }, frame);
        this.bridge.send(packet);
    }
}
